import { useMsal } from '@azure/msal-react';
import _ from 'lodash';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Button, Col, Drawer, Modal, Row } from 'antd';

import { CopyOutlined } from '@ant-design/icons';
import { RootState } from '../../../../application/store';
import { toggleCheckoutDrawer } from '../../../../slices/System/systemSlice';
import {
	initWideFormOrderLine,
	initWideFormProduct,
	toggleWideFormDrawer,
} from '../../../../slices/WideForm/wideFormSlice';
import { ORDER_STATUS } from '../../../../utils/constants/order-status';
import { formatStandardDate } from '../../../../utils/data-format-enchanters/datetime-enchanter';
import {
	groupProductsByFacilityDate,
	orderTotalByProducts,
} from '../../../../utils/data-format-enchanters/order-products-enchanter';

import { useCustomerOrders } from '../../../hooks/useCustomerOrders';
import { useOrder } from '../../../hooks/useOrder';

import './bv-checkout-drawer.scss';

import BVContainerLoader from '../../BVContainerLoader/bv-container-loader';
import BVEmpty from '../../BVEmpty/bv-empty';
import BVOrderStatus from '../../BVOrderStatus/bv-order-status';
import BVPasteExcelDialog from '../BVBasketDrawer/components/BVPasteExcelDialog/bv-paste-excel-dialog';
import WideFormDrawer from '../WideFormDrawer/wide-form-drawer';
import BVShoppingDateGroup from './components/BVShoppingDateGroup/bv-shopping-date-group';
import CheckoutFooter from './components/CheckoutFooter/checkout-footer';
import CheckoutTable from './components/CheckoutTable/checkout-table';
import CheckoutTableSap from './components/CheckoutTableSap/checkout-table-sap';
import CheckoutToolbar from './components/CheckoutToolbar/checkout-toolbar';
import ShoppingCompleteDialog from './components/ShoppingCompleteDialog/shopping-complete-dialog';

const BVCheckoutDrawer: React.FunctionComponent = () => {
	const { accounts } = useMsal();

	const {
		// CRUD Operations
		handleRetrieveOrder,
		retrieveOrderLoading,
		handleUpsertOrder,
		upsertOrderLoading,
		handleDeleteOrder,
		deleteOrderLoading,
		handleExportOrder,
		exportOrderLoading,
		handleRepriceOrder,
		repriceOrderLoading,
	} = useOrder();

	const { handleOrdersSummary } = useCustomerOrders();

	const bvCustomer = useSelector((state: RootState) => state.customer);
	const bvOrderSummary = useSelector((state: RootState) => state.orderSummary);
	const bvSystem = useSelector((state: RootState) => state.system);
	const bvWideForm = useSelector((state: RootState) => state.wideForm);

	const dispatch = useDispatch();

	const [displayMode, setDisplayMode] = useState<'table' | 'sap' | 'list'>(
		'list',
	);
	const [open, setOpen] = useState(false);

	const groupByFacilityDate = groupProductsByFacilityDate(
		bvOrderSummary.products,
	);

	const [completeDialogVisible, setCompleteDialogVisible] =
		useState<boolean>(false);
	const [repriceModalVisibile, setRepriceModalVisibile] =
		useState<boolean>(false);

	const history = useHistory();

	const _customerBusinessUnitComparer = () =>
		bvCustomer &&
		(_.includes(bvCustomer.posAssortment, 'J') ||
			_.includes(bvCustomer.posAssortment, 'W'));

	return (
		<Drawer
			className="bv-checkout-drawer-container"
			width={'100%'}
			placement="right"
			closable={true}
			footer={
				<CheckoutFooter
					actionOrderLoading={
						retrieveOrderLoading || upsertOrderLoading || deleteOrderLoading
					}
					onComplete={() => setCompleteDialogVisible(true)}
					onUpsert={() =>
						handleUpsertOrder(bvOrderSummary, (orderId) =>
							handleRetrieveOrder(orderId),
						)
					}
				/>
			}
			visible={bvSystem.checkoutDrawerVisible}
			onClose={() => {
				setDisplayMode('list');
				dispatch(toggleCheckoutDrawer(false));
			}}
		>
			<div className="bv-checkout-container">
				<Row gutter={[0, 8]}>
					<Modal
						title={`Are you sure you want to perform repricing for the current order?`}
						maskClosable={false}
						closable={false}
						confirmLoading={repriceOrderLoading}
						visible={repriceModalVisibile}
						okText={'Confirm Reprice'}
						cancelText={'Cancel'}
						onCancel={() => {
							setRepriceModalVisibile(false);
						}}
						onOk={() => {
							handleRepriceOrder(bvOrderSummary.id, () =>
								setRepriceModalVisibile(false),
							);
						}}
					/>
					<Col span={3}>
						<span className="checkout-status">
							<div
								className={`checkout-status-wrapper ${bvOrderSummary.status.code === ORDER_STATUS.ERROR.code && 'status-error'}`}
							>
								<BVOrderStatus
									status={bvOrderSummary.status.code}
									title={
										bvOrderSummary.status.code === ORDER_STATUS.ERROR.code
											? 'Some error occours in this order, check the order sap view for more details'
											: ''
									}
								/>
								<span>{bvOrderSummary.status.code}</span>
							</div>
						</span>
					</Col>
					<Col span={9}>
						<span className="checkout-heading">{bvCustomer.fullName}</span>
					</Col>
					<Col span={12} style={{ textAlign: 'right' }}>
						{bvOrderSummary.products &&
							bvOrderSummary.products.length > 0 &&
							bvOrderSummary.status.code === ORDER_STATUS.DRAFT.code && (
								<React.Fragment>
									<Button
										type="primary"
										icon={<CopyOutlined />}
										onClick={() => setOpen(true)}
										style={{ marginRight: 10 }}
									/>
									<Button
										type="primary"
										disabled={
											retrieveOrderLoading ||
											upsertOrderLoading ||
											deleteOrderLoading
										}
										size={'middle'}
										style={{ marginRight: 10 }}
										onClick={() =>
											handleUpsertOrder(bvOrderSummary, (orderId) =>
												handleRetrieveOrder(orderId),
											)
										}
									>
										{'Save Draft'}
									</Button>
									<Button
										type="primary"
										disabled={
											retrieveOrderLoading ||
											upsertOrderLoading ||
											deleteOrderLoading
										}
										size={'middle'}
										onClick={() => setCompleteDialogVisible(true)}
									>
										{'Complete Order'}
									</Button>
									<BVPasteExcelDialog
										visible={open}
										onClose={() => setOpen(false)}
									/>
								</React.Fragment>
							)}
					</Col>
					<Col span={12}>
						<span className="checkout-secondary-heading">
							{`Created by ${bvOrderSummary.loginNameSalesAgent || (accounts.length > 0 ? accounts[0].name : bvSystem.salesAgent)}, last edit ${formatStandardDate(bvOrderSummary.createdAt)}`}
						</span>
					</Col>
					<Col span={12} style={{ textAlign: 'right' }}>
						<span className="checkout-total">
							{orderTotalByProducts(bvOrderSummary.products).amountFormat}
						</span>
					</Col>
				</Row>

				<ShoppingCompleteDialog
					visible={completeDialogVisible}
					onClose={() => setCompleteDialogVisible(false)}
				/>

				<CheckoutToolbar
					items={[
						{
							type: 'add-product',
							disabled:
								!_customerBusinessUnitComparer() ||
								bvOrderSummary.status.code !== ORDER_STATUS.DRAFT.code,
							action: () => {
								dispatch(toggleCheckoutDrawer(false));

								_.includes(bvCustomer.posAssortment, 'J') &&
								_.includes(bvCustomer.posAssortment, 'W')
									? history.push('/catalog')
									: _.includes(bvCustomer.posAssortment, 'J') &&
										  !_.includes(bvCustomer.posAssortment, 'W')
										? history.push('/catalog/J')
										: history.push('/catalog/W');
							},
						},
						{
							type: 'separator',
						},
						{
							type: 'list-view',
							disabled:
								groupByFacilityDate.length === 0 || displayMode === 'list',
							action: () => setDisplayMode('list'),
						},
						{
							type: 'table-view',
							disabled:
								groupByFacilityDate.length === 0 || displayMode === 'table',
							action: () => setDisplayMode('table'),
						},
						{
							type: 'sap-view',
							disabled:
								groupByFacilityDate.length === 0 ||
								bvOrderSummary.status.code === ORDER_STATUS.DRAFT.code ||
								displayMode === 'sap',
							action: () => setDisplayMode('sap'),
						},
						{
							type: 'separator',
						},
						{
							type: 'export-pdf',
							disabled:
								groupByFacilityDate.length === 0 ||
								exportOrderLoading ||
								upsertOrderLoading,
							action: () => {
								if (bvOrderSummary.status.code !== ORDER_STATUS.DRAFT.code) {
									handleExportOrder(
										bvCustomer.fullName || '',
										bvOrderSummary.id,
									);
								} else {
									handleUpsertOrder(
										bvOrderSummary,
										(orderId) => {
											handleExportOrder(bvCustomer.fullName || '', orderId);
											handleRetrieveOrder(orderId);
										},
										false,
									);
								}
							},
						},
						{
							type: 'separator',
						},
						{
							type: 'delete',
							disabled:
								groupByFacilityDate.length === 0 ||
								!bvOrderSummary.id ||
								bvOrderSummary.status.code !== ORDER_STATUS.DRAFT.code ||
								upsertOrderLoading ||
								deleteOrderLoading,
							action: () => {
								handleDeleteOrder(bvOrderSummary.id, () => {
									let onDeleteSuccess = () => {
										dispatch(toggleCheckoutDrawer(false));

										history.push(`/?customer=${bvCustomer.sapCode}`);
									};

									handleOrdersSummary(
										{ customerId: bvCustomer.id },
										onDeleteSuccess,
									);
								});
							},
						},
						{
							type: 'separator',
						},
						{
							type: 'reprice',
							disabled: !bvOrderSummary.id,
							action: () => {
								setRepriceModalVisibile(true);
							},
						},
					]}
				/>

				{displayMode === 'list' && (
					<BVEmpty
						dataSource={groupByFacilityDate}
						disclaimer={'Your order is currently empty.'}
					>
						{_.map(groupByFacilityDate, (dataItem, dataIndex) => (
							<BVShoppingDateGroup
								key={dataIndex}
								disabled={
									bvOrderSummary.status.code !== ORDER_STATUS.DRAFT.code
								}
								shoppingDateGroup={dataItem}
							/>
						))}
					</BVEmpty>
				)}
				{displayMode === 'table' && (
					<BVEmpty
						dataSource={bvOrderSummary.products || []}
						disclaimer={'Your order is currently empty.'}
					>
						<CheckoutTable
							disabled={bvOrderSummary.status.code !== ORDER_STATUS.DRAFT.code}
							products={bvOrderSummary.products || []}
						/>
					</BVEmpty>
				)}
				{displayMode === 'sap' && (
					<BVEmpty
						dataSource={bvOrderSummary.products || []}
						disclaimer={'Your order is currently empty.'}
					>
						<CheckoutTableSap orderId={bvOrderSummary.id} />
					</BVEmpty>
				)}

				<WideFormDrawer
					productId={bvWideForm.productId}
					orderLineId={bvWideForm.orderLineId}
					drawerVisible={bvWideForm.wideFormDrawerVisible}
					onDispose={() => {
						dispatch(initWideFormOrderLine(-1));
						dispatch(initWideFormProduct(-1));
						dispatch(toggleWideFormDrawer(false));
					}}
				/>

				<BVContainerLoader
					loading={
						upsertOrderLoading ||
						retrieveOrderLoading ||
						deleteOrderLoading ||
						exportOrderLoading
					}
				/>
			</div>
		</Drawer>
	);
};

export default BVCheckoutDrawer;
